import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { usePremiumGate } from "@/hooks/usePremiumGate";
import { generateFlashcards, type GeneratedFlashcard } from "@/lib/study";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Plus, RotateCcw, Check, X } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/flashcards")({
  component: FlashcardsPage,
  head: () => ({
    meta: [
      { title: "Flashcards - Inquira" },
      { name: "description", content: "AI-generated spaced repetition flashcards to help you master any concept." },
      { property: "og:title", content: "Flashcards - Inquira" },
      { property: "og:description", content: "Study smarter with spaced repetition flashcards generated from your solved problems." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/flashcards" }],
  }),
});

interface Card {
  id: string;
  front: string;
  back: string;
  concept: string | null;
  next_review_at: string;
  reviews_count: number;
}

function FlashcardsPage() {
  const { user, isLoading, isPaid } = useAuth();
  const { language } = useLanguage();
  const { requirePremium } = usePremiumGate();
  const navigate = useNavigate();
  const [due, setDue] = useState<Card[]>([]);
  const [all, setAll] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);
  const [idx, setIdx] = useState(0);
  const [concept, setConcept] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  async function load() {
    if (!user) return;
    setLoading(true);
    const nowIso = new Date().toISOString();
    const [dueRes, allRes] = await Promise.all([
      supabase.from("flashcards").select("*").eq("user_id", user.id).lte("next_review_at", nowIso).order("next_review_at").limit(50),
      supabase.from("flashcards").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(60),
    ]);
    setDue((dueRes.data as Card[]) ?? []);
    setAll((allRes.data as Card[]) ?? []);
    setLoading(false);
    setIdx(0); setFlipped(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  async function review(quality: 0 | 3 | 5) {
    const card = due[idx];
    if (!card) return;
    const { error } = await supabase.rpc("review_flashcard" as never, { _card_id: card.id, _quality: quality } as never);
    if (error) { toast.error(error.message); return; }
    const { recordProgressEvent } = await import("@/lib/progress");
    await recordProgressEvent({
      type: "flashcard_reviewed",
      subject: card.concept ?? null,
      correct: quality >= 3 ? 1 : 0,
      total: 1,
    });
    setFlipped(false);
    if (idx + 1 >= due.length) { toast.success("All caught up!"); load(); } else { setIdx(idx + 1); }
  }

  async function generate() {
    if (!user) return;
    if (!requirePremium("flashcard_generation")) return;
    const c = concept.trim();
    if (c.length < 2) { toast.error("Enter a concept"); return; }
    setGenerating(true);
    try {
      const { cards } = await generateFlashcards(c, 5, language.code);
      const rows = cards.map((k: GeneratedFlashcard) => ({
        user_id: user.id,
        front: k.front.slice(0, 500),
        back: k.back.slice(0, 2000),
        concept: c.slice(0, 100),
      }));
      const { error } = await supabase.from("flashcards").insert(rows);
      if (error) throw error;
      toast.success(`Added ${rows.length} cards`);
      setConcept("");
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setGenerating(false); }
  }

  if (isLoading || !user) return <main className="min-h-screen bg-background p-6"><p className="text-muted-foreground">Loading…</p></main>;

  const current = due[idx];

  return (
    <main className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <h1 className="text-lg font-bold">Flashcards</h1>
          <span className="text-xs text-muted-foreground">{due.length} due</span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-6 p-4">
        <section className="rounded-2xl border border-border bg-card p-4">
          <label className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" /> Generate cards from a concept
          </label>
          <div className="flex gap-2">
            <input
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              placeholder="e.g. Quadratic formula, mitosis stages"
              maxLength={200}
              className="flex-1 rounded-lg border border-border bg-background p-2 text-sm"
            />
            <Button onClick={generate} disabled={generating}>
              {generating ? "Generating…" : (<><Plus className="mr-1 h-4 w-4" /> Add 5</>)}
            </Button>
          </div>
          {!isPaid && <p className="mt-2 text-xs text-muted-foreground">AI generation is a Pro feature. <Link to="/upgrade" className="underline">Upgrade</Link>.</p>}
        </section>

        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Review</h2>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : current ? (
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              {current.concept && <p className="mb-2 text-xs uppercase tracking-wide text-primary">{current.concept}</p>}
              <div className="min-h-32 rounded-xl bg-muted p-6 text-center text-lg">
                {flipped ? current.back : current.front}
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {!flipped ? (
                  <Button onClick={() => setFlipped(true)} variant="secondary" className="w-full sm:w-auto">
                    <RotateCcw className="mr-1 h-4 w-4" /> Show answer
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => review(0)} variant="destructive"><X className="mr-1 h-4 w-4" /> Again</Button>
                    <Button onClick={() => review(3)} variant="secondary">Hard</Button>
                    <Button onClick={() => review(5)}><Check className="mr-1 h-4 w-4" /> Easy</Button>
                  </>
                )}
              </div>
              <p className="mt-3 text-center text-xs text-muted-foreground">Card {idx + 1} of {due.length}</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              {all.length === 0 ? "No flashcards yet — generate some above." : "No cards due right now. Come back later!"}
            </div>
          )}
        </section>

        {all.length > 0 && (
          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">All cards ({all.length})</h2>
            <div className="space-y-2">
              {all.map((c) => (
                <div key={c.id} className="rounded-lg border border-border bg-card p-3">
                  <p className="text-sm font-medium">{c.front}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{c.back}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Next review: {new Date(c.next_review_at).toLocaleDateString()} · reviewed {c.reviews_count}×
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}